import { useParams } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { tryApiRequest, API_BASE, type BackendPost } from '../lib/api';
import DOMPurify from 'dompurify';
import 'react-quill/dist/quill.snow.css';

export default function NewsDetailPage() {
  const { slug } = useParams();
  const [article, setArticle] = useState<BackendPost | null>(null);
  const [loading, setLoading] = useState(true);
  const tracked = useRef(false);

  useEffect(() => {
    tracked.current = false;
    async function loadArticle() {
      let found: BackendPost | null = null;
      if (slug) {
        const singleRes = await tryApiRequest<{ post: BackendPost }>(`/posts/slug/${slug}`);
        if (singleRes?.post) {
          found = singleRes.post;
        }
      }
      if (!found) {
        const response = await tryApiRequest<{ posts: BackendPost[] }>('/posts?includeContent=true');
        found = response?.posts.find((item) => item.slug === slug) || null;
      }
      if (found) {
        setArticle(found);
        
        // Track post view event
        if (!tracked.current) {
          tracked.current = true;
          fetch(`${API_BASE}/analytics/event`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ eventType: 'view_post', targetId: found.id, targetName: found.title }),
            keepalive: true
          }).catch(err => console.error('Tracking failed', err));
        }
      }
      setLoading(false);
    }

    loadArticle();
  }, [slug]);

  if (loading) {
    return (
      <div style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '12px' }}>⏳</div>
          <p>Đang tải bài viết...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="page-block" style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <h1>Bài viết không tồn tại</h1>
        <p style={{ color: '#94a3b8' }}>Nội dung này có thể đã bị xóa hoặc đường dẫn không đúng.</p>
      </div>
    );
  }

  return (
    <div className="page-block article-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <span className="eyebrow">{article.category}</span>
        <h1 style={{ marginTop: '8px', marginBottom: '12px' }}>{article.title}</h1>
        <p className="article-date" style={{ color: '#94a3b8', margin: 0 }}>
          {new Date(article.publishedAt || article.createdAt || Date.now()).toLocaleDateString('vi-VN')}
        </p>
      </motion.div>

      <motion.div 
        className="article-body ql-editor"
        initial={{ opacity: 0, y: 25 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        style={{
          padding: '36px 40px',
          background: 'var(--glass-bg)',
          backdropFilter: 'blur(20px)',
          border: '1px solid var(--glass-border)',
          borderRadius: '24px',
          boxShadow: '0 16px 40px rgba(0,0,0,0.4)',
          marginTop: '28px'
        }}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(
            article.content.replace(/src="\/uploads\//g, `src="${API_BASE.replace('/api', '')}/uploads/`),
            {
              ADD_TAGS: ['iframe', 'span', 'font'],
              ADD_ATTR: ['style', 'color', 'bgcolor', 'class', 'target', 'allow', 'allowfullscreen', 'frameborder', 'scrolling']
            }
          )
        }}
      />
    </div>
  );
}
